import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Search,
  Send,
  X,
  AlertCircle,
  RefreshCw,
  Mail,
  Plus,
  Minus,
  RotateCcw,
  DollarSign,
  Sparkles,
  CreditCard,
} from "lucide-react";
import Cookies from "js-cookie";

const PAGE_SIZE = 20;

const DEFAULT_CREDITS_PER_PROJECT = 48000;
const DEFAULT_QUESTIONS_PER_PROJECT = 120;
const DEFAULT_CUSTOM_PRICE = 10;
const MIN_CUSTOM_PRICE = 1;
const MAX_PROJECTS_FREEMIUM = 10;
const MAX_PROJECTS_CUSTOM = 50;
const getMaxProjects = (type) =>
  type === "custom" ? MAX_PROJECTS_CUSTOM : MAX_PROJECTS_FREEMIUM;

const fmt = (n) => Number(n || 0).toLocaleString("en-US");

const authedFetch = (path, opts = {}) => {
  const token = Cookies.get("token");
  return fetch(`${process.env.REACT_APP_API_URL}${path}`, {
    ...opts,
    headers: {
      token,
      "Content-Type": "application/json",
      ...(opts.headers || {}),
    },
  });
};

const SearchModal = ({
  searchInput,
  setSearchInput,
  onSubmit,
  onClear,
  onClose,
}) => {
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="user-search-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="user-search-panel">
        <div className="user-search-panel__header">
          <span className="user-search-panel__label">Search invites</span>
          <button className="user-search-panel__close" onClick={onClose}>
            <X size={15} />
          </button>
        </div>
        <div className="user-search-panel__body">
          <div className="user-search-panel__input-wrap">
            <Search size={14} className="user-search-panel__icon" />
            <input
              ref={inputRef}
              className="user-search-panel__input"
              placeholder="Search by email…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onSubmit()}
              autoComplete="off"
            />
            {searchInput && (
              <button className="user-search-panel__clear" onClick={onClear}>
                <X size={12} />
              </button>
            )}
          </div>
          <button className="user-btn user-btn--search-go" onClick={onSubmit}>
            Search
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmModal = ({ target, onConfirm, onCancel, sending }) => {
  const isCustom = target.accountType === "custom";
  const isFollowUp = target.mode === "followup";
  return (
    <div
      className="user-overlay"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="user-confirm">
        <div className="user-confirm__icon">
          {isCustom ? (
            <CreditCard size={28} color="#c9a24a" />
          ) : (
            <AlertCircle size={28} color="#4a9eff" />
          )}
        </div>
        <h3 className="user-confirm__title">
          {isFollowUp
            ? "Send Follow-up Email?"
            : isCustom
              ? "Send Paid Invite?"
              : "Send Free Access Invite?"}
        </h3>
        <p className="user-confirm__body">
          <strong>{target.email}</strong> will receive
          {isFollowUp
            ? " a reminder that their account is pending activation, with a fresh activation link."
            : isCustom
              ? " a payment link to activate their custom account."
              : " an invite link to join the platform for free."}
        </p>

        <div className="acct-confirm-summary">
          <div className="acct-confirm-summary__row">
            <span>Account type</span>
            <span className={`acct-badge acct-badge--${target.accountType}`}>
              {isCustom ? "Custom" : "Freemium"}
            </span>
          </div>
          <div className="acct-confirm-summary__row">
            <span>Projects</span>
            <span>{target.projects}</span>
          </div>
          <div className="acct-confirm-summary__row">
            <span>RU credits</span>
            <span>{fmt(target.credits)}</span>
          </div>
          <div className="acct-confirm-summary__row">
            <span>Avatar questions</span>
            <span>{fmt(target.questionLimit)}</span>
          </div>
          {isCustom && (
            <div className="acct-confirm-summary__row acct-confirm-summary__row--price">
              <span>Price</span>
              <span>${fmt(target.price)}</span>
            </div>
          )}
        </div>

        <div className="user-confirm__actions">
          <button
            className="user-btn user-btn--ghost"
            onClick={onCancel}
            disabled={sending}
          >
            No, Go Back
          </button>
          <button
            className="user-btn user-btn--primary"
            onClick={onConfirm}
            disabled={sending}
          >
            {sending
              ? "Sending…"
              : isFollowUp
                ? "Yes, Send Follow-up"
                : isCustom
                  ? "Yes, Send Payment Invite"
                  : "Yes, Send Invite"}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdjustPlanModal = ({
  target,
  onProjectsChange,
  onCreditsChange,
  onQuestionsChange,
  onResetCredits,
  onResetQuestions,
  onSave,
  onCancel,
  saving,
}) => {
  const maxProjectsForTarget = getMaxProjects(target.accountType);
  return (
    <div
      className="user-overlay"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="user-confirm">
        <div className="user-confirm__icon">
          <Sparkles size={28} color="#4a9eff" />
        </div>
        <h3 className="user-confirm__title">Adjust Plan</h3>
        <p className="user-confirm__body">
          Add extra projects, credits, or avatar questions on top of{" "}
          <strong>{target.email}</strong>'s current{" "}
          {target.accountType === "custom" ? "Custom" : "Freemium"} plan.
          They'll get an email letting them know what was added.
        </p>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            width: "100%",
          }}
        >
          <NumberField
            label="Add Projects"
            value={target.addProjects}
            onChange={onProjectsChange}
            min={0}
            max={maxProjectsForTarget}
            helperText={`You can add up to ${maxProjectsForTarget} additional projects.`}
          />
          <NumberField
            label="Add RU Credits"
            value={target.addCredits}
            onChange={onCreditsChange}
            min={0}
            step={1000}
            showReset={target.creditsTouched}
            onReset={onResetCredits}
          />
          <NumberField
            label="Add Avatar Questions"
            value={target.addQuestions}
            onChange={onQuestionsChange}
            min={0}
            step={10}
            showReset={target.questionsTouched}
            onReset={onResetQuestions}
          />
        </div>

        <div className="user-confirm__actions">
          <button
            className="user-btn user-btn--ghost"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="user-btn user-btn--primary"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    sent: { label: "Sent", cls: "user-pill--sent" },
    accepted: { label: "Accepted", cls: "user-pill--accepted" },
    expired: { label: "Expired", cls: "user-pill--expired" },
    pending: { label: "Awaiting Payment", cls: "user-pill--pending" },
  };
  const { label, cls } = map[status] || {
    label: "Sent",
    cls: "user-pill--sent",
  };
  return <span className={`user-pill ${cls}`}>{label}</span>;
};

const AccountTypeBadge = ({ type }) => (
  <span className={`acct-badge acct-badge--${type}`}>
    {type === "custom" ? "Custom" : "Freemium"}
  </span>
);

const NumberField = ({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  prefix,
  onReset,
  showReset,
  helperText,
}) => {
  const atMax = max !== undefined && Number(value || 0) >= max;
  return (
    <div className="acct-field">
      <label className="acct-field__label">
        {label}
        {showReset && (
          <button
            type="button"
            className="acct-field__reset"
            onClick={onReset}
            title="Reset to default"
          >
            <RotateCcw size={11} />
          </button>
        )}
      </label>
      <div className="acct-stepper">
        {prefix && <span className="acct-stepper__prefix">{prefix}</span>}
        <button
          type="button"
          className="acct-stepper__btn"
          onClick={() => onChange(Math.max(min, Number(value || 0) - step))}
        >
          <Minus size={12} />
        </button>
        <input
          type="number"
          className="acct-stepper__input"
          value={value}
          min={min}
          max={max}
          onChange={(e) => {
            if (e.target.value === "") {
              onChange("");
              return;
            }
            let n = Number(e.target.value);
            if (max !== undefined) n = Math.min(max, n);
            n = Math.max(min, n);
            onChange(n);
          }}
        />
        <button
          type="button"
          className="acct-stepper__btn"
          disabled={atMax}
          onClick={() => {
            const next = Number(value || 0) + step;
            onChange(max !== undefined ? Math.min(max, next) : next);
          }}
        >
          <Plus size={12} />
        </button>
      </div>
      {(helperText || atMax) && (
        <span className="acct-field__hint">
          {atMax ? `Maximum is ${fmt(max)}.` : helperText}
        </span>
      )}
    </div>
  );
};

const UserInvites = () => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjustSaving, setAdjustSaving] = useState(false);

  const [defaults, setDefaults] = useState({
    creditsPerProject: DEFAULT_CREDITS_PER_PROJECT,
    avatarQuestionsPerProject: DEFAULT_QUESTIONS_PER_PROJECT,
  });

  // ---- Invite form state ----
  const [accountType, setAccountType] = useState("freemium");
  const [newEmail, setNewEmail] = useState("");
  const [projects, setProjects] = useState(0);
  const [credits, setCredits] = useState(0);
  const [questionLimit, setQuestionLimit] = useState(0);
  const [price, setPrice] = useState("");
  const [creditsTouched, setCreditsTouched] = useState(false);
  const [questionsTouched, setQuestionsTouched] = useState(false);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await authedFetch("/superadmin/invites/defaults");
        const json = await res.json();
        if (json.success) {
          const merged = {
            creditsPerProject:
              json.data?.creditsPerProject || DEFAULT_CREDITS_PER_PROJECT,
            avatarQuestionsPerProject:
              json.data?.avatarQuestionsPerProject ||
              DEFAULT_QUESTIONS_PER_PROJECT,
          };
          setDefaults(merged);
          setCredits(Number(projects) * merged.creditsPerProject);
          setQuestionLimit(Number(projects) * merged.avatarQuestionsPerProject);
        }
      } catch (err) {
        console.error("Failed to fetch invite defaults", err);
      }
    })();
  }, []);

  /* ---- Fetch invites ---- */
  const fetchInvites = useCallback(async (currentSearch = "") => {
    setLoading(true);
    try {
      const res = await authedFetch("/superadmin/invites");
      const json = await res.json();
      if (json.success) {
        const all = json.data || [];
        const filtered = currentSearch
          ? all.filter((i) =>
              i.email?.toLowerCase().includes(currentSearch.toLowerCase()),
            )
          : all;
        setInvites(filtered);
      }
    } catch (err) {
      console.error("Failed to fetch invites", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvites(search);
  }, []);

  const handleSearchSubmit = () => {
    setSearch(searchInput);
    setPage(1);
    setSearchOpen(false);
    fetchInvites(searchInput);
  };

  const handleSearchClear = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
    setSearchOpen(false);
    fetchInvites("");
  };

  const minProjects = accountType === "custom" ? 1 : 0;
  const maxProjects = getMaxProjects(accountType);

  const maxCredits =
    maxProjects * (defaults.creditsPerProject || DEFAULT_CREDITS_PER_PROJECT);
  const maxQuestions =
    maxProjects *
    (defaults.avatarQuestionsPerProject || DEFAULT_QUESTIONS_PER_PROJECT);

  const handleProjectsChange = (val) => {
    const n =
      val === ""
        ? ""
        : Math.min(maxProjects, Math.max(minProjects, Number(val)));
    setProjects(n);
    if (n === "") return;
    const creditsPerProject =
      defaults.creditsPerProject || DEFAULT_CREDITS_PER_PROJECT;
    const questionsPerProject =
      defaults.avatarQuestionsPerProject || DEFAULT_QUESTIONS_PER_PROJECT;
    if (!creditsTouched) setCredits(n * creditsPerProject);
    if (!questionsTouched) setQuestionLimit(n * questionsPerProject);
  };

  const handleCreditsChange = (val) => {
    setCredits(val === "" ? "" : Math.min(maxCredits, Number(val)));
    setCreditsTouched(true);
  };

  const handleQuestionsChange = (val) => {
    setQuestionLimit(val === "" ? "" : Math.min(maxQuestions, Number(val)));
    setQuestionsTouched(true);
  };

  const currentProjects = () =>
    Number.isFinite(Number(projects)) && projects !== ""
      ? Number(projects)
      : minProjects;

  const resetCredits = () => {
    setCredits(currentProjects() * defaults.creditsPerProject);
    setCreditsTouched(false);
  };

  const resetQuestions = () => {
    setQuestionLimit(currentProjects() * defaults.avatarQuestionsPerProject);
    setQuestionsTouched(false);
  };

  useEffect(() => {
    if (accountType === "custom") {
      if (Number(projects) < 1) handleProjectsChange(1);
      if (!price) {
        setPrice(DEFAULT_CUSTOM_PRICE);
      } else if (Number(price) < MIN_CUSTOM_PRICE) {
        setPrice(MIN_CUSTOM_PRICE);
      }
    } else {
      setPrice("");
    }
  }, [accountType]);

  const resetForm = () => {
    setNewEmail("");
    setProjects(minProjects);
    setCredits(minProjects * defaults.creditsPerProject);
    setQuestionLimit(minProjects * defaults.avatarQuestionsPerProject);
    setCreditsTouched(false);
    setQuestionsTouched(false);
    setPrice(accountType === "custom" ? DEFAULT_CUSTOM_PRICE : "");
  };

  /* ---- Send invite ---- */
  const handleSendClick = () => {
    const email = newEmail.trim();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      showToast("Please enter a valid email address.", "error");
      return;
    }
    if (projects === "" || Number(projects) < minProjects) {
      showToast(
        accountType === "custom"
          ? "Custom accounts need at least 1 project."
          : "Number of projects can't be negative.",
        "error",
      );
      return;
    }
    if (Number(projects) > maxProjects) {
      showToast(`Projects can't exceed ${maxProjects}.`, "error");
      return;
    }
    if (
      accountType === "custom" &&
      (!price || Number(price) < MIN_CUSTOM_PRICE)
    ) {
      showToast(
        `Custom accounts require a price of at least $${MIN_CUSTOM_PRICE}.`,
        "error",
      );
      return;
    }

    setConfirmTarget({
      email,
      accountType,
      projects: Number(projects),
      credits: Math.min(maxCredits, Number(credits) || 0),
      questionLimit: Math.min(maxQuestions, Number(questionLimit) || 0),
      price: accountType === "custom" ? Number(price) : 0,
    });
  };

  const handleSendFollowUp = async () => {
    setSending(true);
    try {
      const res = await authedFetch(
        `/superadmin/invites/${confirmTarget.id}/follow-up`,
        { method: "POST" },
      );
      const json = await res.json();

      if (json.success) {
        showToast(json.message || "Follow-up email sent successfully!");
        await fetchInvites(search);
      } else {
        showToast(json.message || "Failed to send follow-up email.", "error");
      }
    } catch (err) {
      console.error("Send follow-up failed", err);
      showToast("Server error. Please try again.", "error");
    } finally {
      setSending(false);
      setConfirmTarget(null);
    }
  };

  const handleConfirmSend = async () => {
    if (!confirmTarget) return;
    if (confirmTarget.mode === "followup") return handleSendFollowUp();
    setSending(true);
    try {
      const isCustom = confirmTarget.accountType === "custom";
      const endpoint = isCustom
        ? "/superadmin/sendCustomInvite"
        : "/superadmin/sendFreemiumInvite";
      const body = {
        email: confirmTarget.email,
        projectLimits: confirmTarget.projects,
        credits: confirmTarget.credits,
        questionLimit: confirmTarget.questionLimit,
        ...(isCustom ? { price: confirmTarget.price } : {}),
      };

      const res = await authedFetch(endpoint, {
        method: "POST",
        body: JSON.stringify(body),
      });
      const json = await res.json();

      if (json.success) {
        showToast(
          json.message ||
            (isCustom
              ? "Payment invite sent successfully!"
              : "Invite sent successfully!"),
        );
        resetForm();
        await fetchInvites(search);
      } else {
        showToast(json.message || "Failed to send invite.", "error");
      }
    } catch (err) {
      console.error("Send invite failed", err);
      showToast("Server error. Please try again.", "error");
    } finally {
      setSending(false);
      setConfirmTarget(null);
    }
  };

  const handleResend = (inv) => {
    setConfirmTarget({
      mode: "followup",
      id: inv._id || inv.id,
      email: inv.email,
      accountType: inv.accountType || "freemium",
      projects: inv.projectLimits || 1,
      credits: inv.credits,
      questionLimit: inv.questionLimit,
      price: inv.price || 0,
    });
  };

  const handleEditInvite = (inv) => {
    const type = inv.accountType || "freemium";
    const maxProjectsForType = getMaxProjects(type);
    const creditsPerProject =
      defaults.creditsPerProject || DEFAULT_CREDITS_PER_PROJECT;
    const questionsPerProject =
      defaults.avatarQuestionsPerProject || DEFAULT_QUESTIONS_PER_PROJECT;
    setAccountType(type);
    setNewEmail(inv.email);
    setProjects(
      Math.min(
        maxProjectsForType,
        inv.projectLimits ?? (type === "custom" ? 1 : 0),
      ),
    );
    setCredits(
      Math.min(maxProjectsForType * creditsPerProject, inv.credits ?? 0),
    );
    setQuestionLimit(
      Math.min(
        maxProjectsForType * questionsPerProject,
        inv.questionLimit ?? 0,
      ),
    );
    setCreditsTouched(true);
    setQuestionsTouched(true);
    setPrice(type === "custom" ? inv.price || MIN_CUSTOM_PRICE : "");
    if (typeof window !== "undefined")
      window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleOpenAdjust = (inv) => {
    setAdjustTarget({
      id: inv._id || inv.id,
      email: inv.email,
      accountType: inv.accountType || "freemium",
      addProjects: 0,
      addCredits: 0,
      addQuestions: 0,
      creditsTouched: false,
      questionsTouched: false,
    });
  };

  const handleAdjustProjectsChange = (val) => {
    setAdjustTarget((prev) => {
      if (!prev) return prev;
      const maxProjectsForTarget = getMaxProjects(prev.accountType);
      const n =
        val === ""
          ? ""
          : Math.min(maxProjectsForTarget, Math.max(0, Number(val)));
      const next = { ...prev, addProjects: n };
      if (n === "") return next;
      const creditsPerProject =
        defaults.creditsPerProject || DEFAULT_CREDITS_PER_PROJECT;
      const questionsPerProject =
        defaults.avatarQuestionsPerProject || DEFAULT_QUESTIONS_PER_PROJECT;
      if (!prev.creditsTouched) next.addCredits = n * creditsPerProject;
      if (!prev.questionsTouched) next.addQuestions = n * questionsPerProject;
      return next;
    });
  };

  const handleAdjustCreditsChange = (val) => {
    setAdjustTarget((prev) => {
      if (!prev) return prev;
      const creditsPerProject =
        defaults.creditsPerProject || DEFAULT_CREDITS_PER_PROJECT;
      const maxAddCredits =
        getMaxProjects(prev.accountType) * creditsPerProject;
      return {
        ...prev,
        addCredits: val === "" ? "" : Math.min(maxAddCredits, Number(val)),
        creditsTouched: true,
      };
    });
  };

  const handleAdjustQuestionsChange = (val) => {
    setAdjustTarget((prev) => {
      if (!prev) return prev;
      const questionsPerProject =
        defaults.avatarQuestionsPerProject || DEFAULT_QUESTIONS_PER_PROJECT;
      const maxAddQuestions =
        getMaxProjects(prev.accountType) * questionsPerProject;
      return {
        ...prev,
        addQuestions: val === "" ? "" : Math.min(maxAddQuestions, Number(val)),
        questionsTouched: true,
      };
    });
  };

  const handleResetAdjustCredits = () => {
    setAdjustTarget((prev) => {
      if (!prev) return prev;
      const n = Number(prev.addProjects) || 0;
      const creditsPerProject =
        defaults.creditsPerProject || DEFAULT_CREDITS_PER_PROJECT;
      return {
        ...prev,
        addCredits: n * creditsPerProject,
        creditsTouched: false,
      };
    });
  };

  const handleResetAdjustQuestions = () => {
    setAdjustTarget((prev) => {
      if (!prev) return prev;
      const n = Number(prev.addProjects) || 0;
      const questionsPerProject =
        defaults.avatarQuestionsPerProject || DEFAULT_QUESTIONS_PER_PROJECT;
      return {
        ...prev,
        addQuestions: n * questionsPerProject,
        questionsTouched: false,
      };
    });
  };

  const handleSaveAdjust = async () => {
    if (!adjustTarget) return;
    const { id, addProjects, addCredits, addQuestions } = adjustTarget;
    const projectsN = Number(addProjects) || 0;
    const creditsN = Number(addCredits) || 0;
    const questionsN = Number(addQuestions) || 0;

    if (projectsN <= 0 && creditsN <= 0 && questionsN <= 0) {
      showToast("Enter at least one value to add.", "error");
      return;
    }

    const maxProjectsForTarget = getMaxProjects(adjustTarget.accountType);
    if (projectsN > maxProjectsForTarget) {
      showToast(
        `You can add up to ${maxProjectsForTarget} projects at a time.`,
        "error",
      );
      return;
    }

    setAdjustSaving(true);
    try {
      const res = await authedFetch("/superadmin/invites/adjustPlan", {
        method: "POST",
        body: JSON.stringify({
          userId: id,
          addProjects: projectsN,
          addCredits: creditsN,
          addQuestions: questionsN,
        }),
      });
      const json = await res.json();
      if (json.success) {
        showToast(json.message || "Plan updated successfully!");
        setAdjustTarget(null);
        await fetchInvites(search);
      } else {
        showToast(json.message || "Failed to update plan.", "error");
      }
    } catch (err) {
      console.error("Adjust plan failed", err);
      showToast("Server error. Please try again.", "error");
    } finally {
      setAdjustSaving(false);
    }
  };

  /* ---- Derived data ---- */
  const totalPages = Math.max(1, Math.ceil(invites.length / PAGE_SIZE));
  const paginated = invites.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stats = useMemo(() => {
    const accepted = invites.filter((i) => i.status === "accepted");
    return [
      { label: "Total", value: invites.length, color: "#4a9eff" },
      {
        label: "Sent",
        value: invites.filter((i) => i.status === "sent").length,
        color: "#94a3b8",
      },
      { label: "Accepted", value: accepted.length, color: "#4adf9a" },
    ];
  }, [invites]);

  const livePreviewLabel = `${projects || 0} project${Number(projects) === 1 ? "" : "s"} → ${fmt(
    credits,
  )} credits · ${fmt(questionLimit)} avatar questions`;

  return (
    <div className="user-page">
      {toast && (
        <div className={`user-toast user-toast--${toast.type}`}>
          {toast.msg}
        </div>
      )}

      <div className="user-page-header">
        <h1 className="user-page-title">Account Invites</h1>
        <p className="user-page-sub">
          Create Freemium or Custom (paid) accounts with configurable credits,
          projects, and avatar questions.
        </p>
      </div>

      {/* ---------------- Invite composer ---------------- */}
      <div className="acct-composer">
        <div className="acct-type-toggle">
          <button
            type="button"
            className={`acct-type-toggle__btn ${accountType === "freemium" ? "acct-type-toggle__btn--active" : ""}`}
            onClick={() => setAccountType("freemium")}
          >
            <Sparkles size={14} /> Freemium
          </button>
          <button
            type="button"
            className={`acct-type-toggle__btn acct-type-toggle__btn--custom ${
              accountType === "custom" ? "acct-type-toggle__btn--active" : ""
            }`}
            onClick={() => setAccountType("custom")}
          >
            <CreditCard size={14} /> Custom (Paid)
          </button>
        </div>

        <div
          className={`acct-composer__grid ${accountType === "custom" ? "acct-composer__grid--price" : ""}`}
        >
          <div className="acct-field acct-field--email">
            <label className="acct-field__label">Email address</label>
            <div className="user-invite-input-wrap">
              <Mail size={15} className="user-invite-input-icon" />
              <input
                className="user-invite-input"
                type="email"
                placeholder="Enter email address to invite…"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendClick()}
              />
              {newEmail && (
                <button
                  className="user-invite-input-clear"
                  onClick={() => setNewEmail("")}
                >
                  <X size={13} />
                </button>
              )}
            </div>
          </div>

          <NumberField
            label="Projects"
            value={projects}
            onChange={handleProjectsChange}
            min={minProjects}
            max={maxProjects}
          />

          <NumberField
            label="RU Credits"
            value={credits}
            onChange={handleCreditsChange}
            min={0}
            max={maxCredits}
            step={defaults.creditsPerProject}
            showReset={creditsTouched}
            onReset={resetCredits}
          />

          <NumberField
            label="Avatar Questions"
            value={questionLimit}
            onChange={handleQuestionsChange}
            min={0}
            max={maxQuestions}
            step={defaults.avatarQuestionsPerProject}
            showReset={questionsTouched}
            onReset={resetQuestions}
          />

          {accountType === "custom" && (
            <NumberField
              label="Price"
              value={price}
              onChange={(val) =>
                setPrice(
                  val === "" ? "" : Math.max(MIN_CUSTOM_PRICE, Number(val)),
                )
              }
              min={MIN_CUSTOM_PRICE}
              step={10}
              prefix={<DollarSign size={13} />}
            />
          )}
        </div>

        <div className="acct-composer__footer">
          <span className="acct-composer__preview">{livePreviewLabel}</span>
          <button
            className="user-btn user-btn--primary"
            onClick={handleSendClick}
            disabled={!newEmail.trim()}
          >
            <Send size={14} />{" "}
            {accountType === "custom" ? "Send Payment Invite" : "Send Invite"}
          </button>
        </div>
      </div>

      {/* ---------------- Stats + search ---------------- */}
      <div className="user-action-row acct-stats-row">
        <div className="user-action-right acct-stats-row__stats">
          {stats.map(({ label, value, color }) => (
            <div className="user-mini-stat" key={label}>
              <span className="user-mini-stat__label">{label}</span>
              <span className="user-mini-stat__value" style={{ color }}>
                {value}
              </span>
            </div>
          ))}
          <button
            className={`user-search-toggle ${searchOpen ? "user-search-toggle--active" : ""}`}
            onClick={() => setSearchOpen(true)}
            title="Search invites"
          >
            <Search size={16} />
            {search && <span className="user-search-toggle__dot" />}
          </button>
        </div>
      </div>

      {search && (
        <div className="user-search-active">
          Showing results for <strong>"{search}"</strong>
          <button
            onClick={handleSearchClear}
            className="user-search-active__clear"
          >
            <X size={12} /> Clear
          </button>
        </div>
      )}

      {/* ---------------- Table ---------------- */}
      <div className="acct-table-wrap">
        <div className="acct-table-head acct-row-grid">
          <div className="acct-col acct-col--email">Email</div>
          <div className="acct-col">Type</div>
          <div className="acct-col">Projects</div>
          <div className="acct-col">Credits</div>
          <div className="acct-col">Avatar Qs</div>
          <div className="acct-col">Price</div>
          <div className="acct-col">Invited On</div>
          <div className="acct-col">Status</div>
          <div className="acct-col">Action</div>
        </div>

        {loading ? (
          <div className="user-empty user-empty--loading">
            <RefreshCw size={18} className="user-spin" /> Loading invites…
          </div>
        ) : paginated.length === 0 ? (
          <div className="user-empty">
            {search
              ? `No invites found for "${search}".`
              : "No invites sent yet. Configure an account above to get started."}
          </div>
        ) : (
          paginated.map((inv) => {
            const id = inv._id || inv.id;
            const isAccepted = inv.status === "accepted";
            const displayStatus =
              inv.accountType === "custom" &&
              !isAccepted &&
              inv.paymentStatus === "pending"
                ? "pending"
                : inv.status;
            return (
              <div className="acct-row acct-row-grid" key={id}>
                <div className="acct-col acct-col--email">
                  <div className="user-email-cell">
                    <div className="user-email-avatar">
                      {inv.email?.[0]?.toUpperCase() ?? "?"}
                    </div>
                    <span className="user-email-text">{inv.email}</span>
                  </div>
                </div>
                <div className="acct-col">
                  <AccountTypeBadge type={inv.accountType || "freemium"} />
                </div>
                <div className="acct-col">{inv.projectLimits ?? "—"}</div>
                <div className="acct-col">{fmt(inv.credits)}</div>
                <div className="acct-col">{fmt(inv.questionLimit)}</div>
                <div className="acct-col">
                  {inv.accountType === "custom" ? (
                    `$${fmt(inv.price)}`
                  ) : (
                    <span className="acct-muted">Free</span>
                  )}
                </div>
                <div className="acct-col">
                  <span className="user-date-text">
                    {inv.sentAt
                      ? new Date(inv.sentAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                  </span>
                </div>
                <div className="acct-col">
                  <StatusPill status={displayStatus} />
                </div>
                <div className="acct-col">
                  {isAccepted ? (
                    <div className="acct-action-stack">
                      <span className="user-action-done">✓ Joined</span>
                      <button
                        className="user-btn user-btn--resend"
                        onClick={() => handleOpenAdjust(inv)}
                      >
                        <Plus size={12} /> Adjust Plan
                      </button>
                    </div>
                  ) : (
                    <div className="acct-action-stack acct-action-stack--row">
                      <button
                        className="user-btn user-btn--ghost"
                        onClick={() => handleEditInvite(inv)}
                      >
                        Edit
                      </button>
                      <button
                        className="user-btn user-btn--resend"
                        onClick={() => handleResend(inv)}
                      >
                        <Send size={13} /> Resend
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {totalPages > 1 && (
        <div className="user-pagination">
          <button
            className="user-page-btn"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(
              (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2,
            )
            .reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1) acc.push("…");
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) =>
              p === "…" ? (
                <span key={`el-${i}`} className="user-page-ellipsis">
                  …
                </span>
              ) : (
                <button
                  key={p}
                  className={`user-page-btn ${page === p ? "user-page-btn--active" : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ),
            )}
          <button
            className="user-page-btn"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}

      {searchOpen && (
        <SearchModal
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          onSubmit={handleSearchSubmit}
          onClear={handleSearchClear}
          onClose={() => setSearchOpen(false)}
        />
      )}

      {confirmTarget && (
        <ConfirmModal
          target={confirmTarget}
          onConfirm={handleConfirmSend}
          onCancel={() => setConfirmTarget(null)}
          sending={sending}
        />
      )}

      {adjustTarget && (
        <AdjustPlanModal
          target={adjustTarget}
          onProjectsChange={handleAdjustProjectsChange}
          onCreditsChange={handleAdjustCreditsChange}
          onQuestionsChange={handleAdjustQuestionsChange}
          onResetCredits={handleResetAdjustCredits}
          onResetQuestions={handleResetAdjustQuestions}
          onSave={handleSaveAdjust}
          onCancel={() => setAdjustTarget(null)}
          saving={adjustSaving}
        />
      )}
    </div>
  );
};

export default UserInvites;
